import type { ReactNode } from "react";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";

/** A titled card of the settings page. */
export function SettingsCard({
  id,
  title,
  action,
  children,
  className,
}: {
  id: string;
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section aria-labelledby={id} className={cn("rounded-lg border border-border/60 bg-surface", className)}>
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3 sm:px-5">
        <h2 id={id} className="text-sm font-semibold text-foreground">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** One line of a card: an icon, a label with its hint, and what to do about it on the right. */
export function SettingsRow({
  icon,
  label,
  hint,
  children,
  tone = "default",
  onClick,
}: {
  icon: IconName;
  label: ReactNode;
  hint?: ReactNode;
  /** The control on the right. Ignored when the whole row is a button. */
  children?: ReactNode;
  tone?: "default" | "danger";
  onClick?: () => void;
}) {
  const body = (
    <>
      <span
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-md",
          tone === "danger" ? "bg-danger/10 text-danger" : "bg-surface-2 text-muted-foreground",
        )}
      >
        <Icon name={icon} className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block text-sm font-medium", tone === "danger" ? "text-danger" : "text-foreground")}>{label}</span>
        {hint ? <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{hint}</span> : null}
      </span>
    </>
  );

  if (onClick) {
    return (
      <li>
        <button
          type="button"
          onClick={onClick}
          className="group flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:px-5"
        >
          {body}
          <Icon name="chevronRight" className="size-4 shrink-0 text-faint-foreground transition-transform group-hover:translate-x-0.5" />
        </button>
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3.5 sm:flex-nowrap sm:px-5">
      {body}
      {children ? <div className="flex w-full justify-end sm:w-auto sm:shrink-0">{children}</div> : null}
    </li>
  );
}

export function SettingsRows({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-border/60">{children}</ul>;
}

/** A small rounded label: account status, role. */
export function Chip({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "success" | "danger" | "brand" }) {
  const tones = {
    default: "border-border/60 text-muted-foreground",
    success: "border-success/30 bg-success/10 text-success",
    danger: "border-danger/30 bg-danger/10 text-danger",
    brand: "border-brand/30 bg-brand/10 text-brand",
  };
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium", tones[tone])}>{children}</span>
  );
}
